/**
 * Mi Espacio: lo que le toca hacer a quien está mirando la pantalla.
 *
 * Nace de un problema concreto: recepción cobra, y el paciente queda esperando
 * a que enfermería lo llame a triaje — pero enfermería no se enteraba de nada.
 * Cada bandeja es el relevo del turno anterior. Qué bandejas ves lo deciden tus
 * permisos, así que la misma persona que cobra y tría ve las dos.
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  CalendarPlus,
  CalendarClock,
  CheckCircle2,
  FlaskConical,
  FileText,
  PackageSearch,
  Pill,
  Stethoscope,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { getMiEspacio, type Bandeja, type ItemBandeja } from "../api/miEspacio";
import { NIVELES_URGENCIA } from "../components/atencion/TriajeDialog";
import { useAuth } from "../auth/AuthProvider";
import { useBase, useZonaHoraria } from "../auth/TenantBrandingProvider";
import { diaEnZona, fechaHora, fechaLarga, hora, hoyEnZona } from "../lib/fecha";
import { Alert, Badge, Card, CardBody, CardHeader } from "../components/ui";

const ICONO: Record<string, LucideIcon> = {
  cobrar: Wallet,
  solicitudes: CalendarPlus,
  validar_pago: Wallet,
  triaje: Activity,
  atender: Stethoscope,
  agendar: CalendarPlus,
  dispensar: Pill,
  reponer: PackageSearch,
  agendar_sesiones: CalendarClock,
  lab_cobrar: Wallet,
  lab_procesar: FlaskConical,
  lab_agendar: CalendarPlus,
  lab_resultados: FileText,
};

const ACCION: Record<string, string> = {
  cobrar: "Cobrar",
  solicitudes: "Dar hora",
  validar_pago: "Revisar pago",
  triaje: "Tomar triaje",
  atender: "Atender",
  agendar: "Agendar",
  dispensar: "Despachar",
  reponer: "Ver compras",
  agendar_sesiones: "Agendar",
  lab_cobrar: "Cobrar",
  lab_procesar: "Procesar",
  lab_agendar: "Dar hora",
  lab_resultados: "Ver resultados",
};

/**
 * Lo que tiene gente esperando va arriba; lo vacío, al final.
 *
 * Antes salían en el orden que las mandaba el backend, y una bandeja con CERO
 * —"Derivados sin cita"— se quedaba encima de una con alguien esperando una
 * llamada. Había que bajar para enterarse de que un paciente pidió cita, que es
 * exactamente lo contrario de para lo que sirve esta pantalla.
 *
 * Entre las que tienen trabajo manda la urgencia: alguien que pagó y espera
 * confirmación, o que pidió cita y espera una llamada, va antes que un examen
 * al que hay que dar hora cuando se pueda.
 */
const URGENCIA: Record<string, number> = {
  validar_pago: 0,
  solicitudes: 1,
  triaje: 2,
  atender: 3,
  cobrar: 4,
  lab_cobrar: 5,
  lab_procesar: 6,
  dispensar: 7,
};

function ordenar(bandejas: Bandeja[]): Bandeja[] {
  return [...bandejas].sort((a, b) => {
    const vacias = Number(a.total === 0) - Number(b.total === 0);
    if (vacias !== 0) return vacias;
    return (URGENCIA[a.codigo] ?? 50) - (URGENCIA[b.codigo] ?? 50);
  });
}

export function MiEspacioPage() {
  const { me } = useAuth();
  const tz = useZonaHoraria();

  const { data, isLoading, error } = useQuery({
    queryKey: ["mi-espacio"],
    queryFn: getMiEspacio,
    // Es una bandeja compartida: mientras recepción cobra, enfermería mira.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const nombre = me?.nombre_para_mostrar ?? "";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Mi Espacio</h1>
        <p className="mt-1 text-sm text-slate-500">
          {nombre && <span>{nombre}</span>}
          {nombre && " · "}
          {fechaLarga(new Date().toISOString(), tz)}
          {data != null && (
            <>
              {" · "}
              {data.total_pendiente === 0
                ? "sin pendientes"
                : `${data.total_pendiente} pendiente${data.total_pendiente === 1 ? "" : "s"}`}
            </>
          )}
        </p>
      </div>

      {isLoading && <div className="text-sm text-slate-400">Cargando…</div>}
      {error && <Alert>No se pudo cargar tu bandeja.</Alert>}

      {data?.bandejas.length === 0 && (
        <Card>
          <CardBody className="py-10 text-center text-sm text-slate-500">
            Tu cuenta no tiene ninguna tarea de atención asignada. Si deberías
            cobrar, triar o atender, pídele al administrador que revise tu rol.
          </CardBody>
        </Card>
      )}

      {ordenar(data?.bandejas ?? []).map((b) => (
        <BandejaCard key={b.codigo} bandeja={b} />
      ))}
    </div>
  );
}

function BandejaCard({ bandeja }: { bandeja: Bandeja }) {
  const Icon = ICONO[bandeja.codigo] ?? Activity;
  const atrasadas = bandeja.items.filter((i) => i.atrasada).length;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-full ${
              bandeja.total > 0
                ? "bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)]"
                : "bg-slate-100 text-slate-400"
            }`}
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="font-semibold text-slate-900">{bandeja.titulo}</h2>
            <p className="text-xs text-slate-500">{bandeja.descripcion}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {atrasadas > 0 && <Badge variant="danger">{atrasadas} atrasado</Badge>}
            <span
              className={`min-w-[1.75rem] rounded-full px-2.5 py-0.5 text-center text-sm font-semibold ${
                bandeja.total > 0
                  ? "bg-red-600 text-white"
                  : "bg-slate-100 text-slate-500"
              }`}
            >
              {bandeja.total}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {bandeja.total === 0 ? (
          <Vacia bandeja={bandeja} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {bandeja.items.map((it) => (
              <Fila
                key={`${bandeja.codigo}-${
                  it.cita_id ??
                  it.receta_id ??
                  (it.orden_lab_id != null
                    ? `l${it.orden_lab_id}`
                    : // La reposición no tiene id propio: es un producto
                      // agrupado, y su nombre es lo único que la identifica.
                      it.asignacion_id ?? it.paciente_nombre)
                }`}
                item={it}
                codigo={bandeja.codigo}
              />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

/** Una bandeja vacía sin contexto se lee como avería; esta dice qué viene. */
function Vacia({ bandeja }: { bandeja: Bandeja }) {
  const tz = useZonaHoraria();
  const base = useBase();
  return (
    <div className="px-6 py-8 text-center">
      <CheckCircle2 className="mx-auto mb-2 h-6 w-6 text-[color:var(--color-link)]" />
      <p className="text-sm text-slate-600">Nada pendiente ahora.</p>
      {bandeja.proximo && (
        <p className="mt-1 text-xs text-slate-500">
          Lo siguiente:{" "}
          {bandeja.proximo.cita_id ? (
            <Link
              to={`${base}/citas/${bandeja.proximo.cita_id}`}
              className="font-medium text-[color:var(--color-link)] hover:underline"
            >
              {bandeja.proximo.inicio && fechaHora(bandeja.proximo.inicio, tz)} ·{" "}
              {bandeja.proximo.paciente_nombre} · {bandeja.proximo.titulo}
            </Link>
          ) : (
            <>
              {bandeja.proximo.paciente_nombre} · {bandeja.proximo.titulo}
            </>
          )}
        </p>
      )}
    </div>
  );
}

function Fila({ item, codigo }: { item: ItemBandeja; codigo: Bandeja["codigo"] }) {
  const base = useBase();
  const tz = useZonaHoraria();
  const { me } = useAuth();
  // Caja ve a quién cobrarle, pero la ficha del paciente no es asunto suyo:
  // sin el permiso el nombre es texto, no un enlace que acabaría en un 403.
  const puedeVerFicha =
    me?.is_super_admin || (me?.permisos.includes("paciente:read") ?? false);

  // La reposición no habla de un paciente sino de un producto: su sitio es la
  // pantalla de compras, y su "nombre" es el medicamento que falta.
  const esReposicion = codigo === "reponer";

  // Todo termina en la atención salvo la derivación sin cita, que aún no la tiene.
  const destino = esReposicion
    ? `${base}/farmacia/compras`
    : item.solicitud_id
    ? // Pidió cita desde su app: se va a agendar, y la solicitud viaja en la
      // URL para poder cerrarla con la cita que salga de ahí. Sin eso, dar
      // hora deja la petición abierta en la bandeja para siempre.
      `${base}/citas?solicitud=${item.solicitud_id}&paciente=${item.paciente_id}`
    : item.orden_lab_id
    ? // Cada bandeja de laboratorio lleva a donde se hace ese trabajo: caja
      // cobra, el laboratorio procesa y el médico lee el resultado en la ficha
      // del paciente, que es donde está la atención que lo pidió.
      codigo === "lab_cobrar"
      ? `${base}/caja?orden_lab=${item.orden_lab_id}`
      : codigo === "lab_procesar"
        ? `${base}/laboratorio`
        : codigo === "lab_agendar"
          ? `${base}/pacientes/${item.paciente_id}`
        : `${base}/pacientes/${item.paciente_id}`
    : item.plan_id
    ? // Las sesiones se agendan desde la ficha del paciente, que es donde está
      // el plan con su progreso.
      `${base}/pacientes/${item.paciente_id}`
    : item.receta_id
    ? // La receta se despacha en el mostrador, no en la ficha de la cita.
      `${base}/farmacia/despacho`
    : item.cita_id
    ? codigo === "cobrar"
      ? `${base}/caja?cita=${item.cita_id}`
      : codigo === "triaje"
        ? // Directo al formulario. Desde la bandeja, "Tomar triaje" tiene que
          // abrir el triaje: llevar a la ficha y obligar a buscar otra vez el
          // botón es hacerle dar dos vueltas a quien tiene al paciente al lado.
          `${base}/citas/${item.cita_id}?triaje=1`
        : `${base}/citas/${item.cita_id}`
    : puedeVerFicha
      ? `${base}/pacientes/${item.paciente_id}`
      : `${base}/mi-espacio`;

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-6 py-3">
      {/* La hora sola no basta: la bandeja arrastra varios días y ocho fechas
          distintas se leían como si fueran todas de hoy. */}
      <span
        className={`w-24 shrink-0 font-mono text-sm leading-tight ${
          item.atrasada ? "font-semibold text-red-600" : "text-slate-500"
        }`}
      >
        {item.inicio ? hora(item.inicio, tz) : "—"}
        {item.inicio && diaEnZona(item.inicio, tz) !== hoyEnZona(tz) && (
          <span className="block text-[11px] font-normal">
            {new Date(item.inicio).toLocaleDateString("es-PE", {
              timeZone: tz,
              day: "2-digit",
              month: "2-digit",
            })}
          </span>
        )}
      </span>
      {puedeVerFicha && !esReposicion ? (
        <Link
          to={`${base}/pacientes/${item.paciente_id}`}
          className="font-medium text-slate-900 hover:text-[color:var(--color-link)] hover:underline"
        >
          {item.paciente_nombre}
        </Link>
      ) : (
        <span className="font-medium text-slate-900">{item.paciente_nombre}</span>
      )}
      <span className="text-sm text-slate-500">
        {item.titulo}
        {item.detalle && ` · ${item.detalle}`}
      </span>
      {item.nivel_urgencia != null && (
        <span
          className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
            NIVELES_URGENCIA[item.nivel_urgencia]?.color ?? "bg-slate-100"
          }`}
          title="Nivel de urgencia del triaje"
        >
          {NIVELES_URGENCIA[item.nivel_urgencia]?.label ?? item.nivel_urgencia}
        </span>
      )}
      {item.monto != null && (
        <span className="font-mono text-sm text-slate-700">
          S/ {Number(item.monto).toFixed(2)}
        </span>
      )}
      <Link
        to={destino}
        className="ml-auto inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-[color:var(--color-link)] hover:bg-[color-mix(in_srgb,var(--color-link)_8%,white)]"
      >
        {ACCION[codigo] ?? "Abrir"}
        <ArrowRight className="h-4 w-4" />
      </Link>

      {/* El médico llama al paciente sabiendo con qué llega. Si nadie lo trió,
          lo dice: es un dato clínico faltante, no un espacio en blanco. */}
      {codigo === "atender" && (
        <p className="w-full pl-16 text-xs text-slate-500">
          {item.triaje_id ? (
            <>
              {item.signos ?? "Triaje sin signos vitales registrados"}
              {item.motivo && (
                <span className="italic text-slate-400"> — {item.motivo}</span>
              )}
            </>
          ) : (
            <span className="text-amber-700">Entra sin triaje.</span>
          )}
        </p>
      )}
    </li>
  );
}
